// Basic
import { useState, useEffect } from 'react';
import { Navigate, useLocation, useParams } from 'react-router-dom';
// Store & actions
import actionsRentalFiles from '@/app/modules/rentalFiles/api/actionsRentalFiles';
import useRentalFilesStore from '@/app/store/modules/rentalFiles';
// Components
import Page from '@/common-utils/components/structure/page/Page';
import RentalFileInfosPanel from '@/app/modules/rentalFiles/components/details/RentalFileInfosPanel';
import RentalFileHistoryPanel from '@/app/modules/rentalFiles/components/details/history/RentalFileHistoryPanel';
import Spinner from '@/common-utils/components/renders/spinner/Spinner';
import Entity from '@/common-utils/components/renders/entity/Entity';
import Icon from '@/common-utils/components/renders/icon/Icon';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/common-utils/components/ui/accordion';
// Common
import RouteUrls from '../../enums/RouteUrls';
import { extractQueriesFromUrl, replaceParameters } from '@/common-utils/functions/restUtils';
import { findApplicationFromCode } from '@/app/modules/contracts/enums/ItpRegex';
import remoteCollectionFields from '@/app/modules/remoteCollections/fields/remoteCollectionFields';
import rentalFilesFields from '@/app/modules/rentalFiles/fields/rentalFilesFields';
import ICONS from '@/common-utils/enums/icons';
// Lang
import { nsRentalFile } from '@/app/i18n/namespaces';

/**
 * @returns {JSX} RentalFileDetails
 */
function RentalFileDetails() {

    // URL Params
    const { remoteCollectionId, rentalFileId } = useParams();
    const location = useLocation();
    const urlQueries = extractQueriesFromUrl(location.search);

    // State
    const [openRentalFileDetails, setOpenRentalFileDetails] = useState(true);
    const [openRentalFileHistory, setOpenRentalFileHistory] = useState(false);
    const [i18nData, setI18nData] = useState({});

    // Store
    // Store the edited remote collection in the store to be able to use it in the breadcrumb
    const storeEditedRemoteCollection = useRentalFilesStore(s => s.storeEditedRemoteCollection);
    const getEditedRemoteCollection = useRentalFilesStore(s => s.getEditedRemoteCollection);
    const storedEditedRemoteCollection = getEditedRemoteCollection(remoteCollectionId);

    // Constants
    const applicationCode = urlQueries[remoteCollectionFields.APPLICATION.key]
        ?? storedEditedRemoteCollection?.applicationCode ?? null;
    const application = applicationCode ? findApplicationFromCode(applicationCode)?.name : '';
    const contract = urlQueries[remoteCollectionFields.CONTRACT.key]
        ?? storedEditedRemoteCollection?.contract ?? null;
    const reconciliationNumber = urlQueries[remoteCollectionFields.RECONCILIATION_NUMBER.key]
        ?? storedEditedRemoteCollection?.reconciliationNumber ?? null;


    // API Calls
    const { data: rentalFile, isFetching, isError, refetch } = actionsRentalFiles.getRentalFile(rentalFileId, true);

    // Hooks
    useEffect(() => {
        const contractNumber = contract ?? rentalFile?.[rentalFilesFields.CONTRACT.key] ?? null;

        // Store the edited remote collection in the store to be able to use it in the breadcrumb
        if (remoteCollectionId && (reconciliationNumber || contractNumber)) {
            storeEditedRemoteCollection(remoteCollectionId, {
                reconciliationNumber,
                contract: contractNumber,
                applicationCode
            });
        }

        // Set the i18n data
        setI18nData({
            number: contractNumber,
            reconciliationNumber,
            application,
            fileNumber: rentalFile?.[rentalFilesFields.FILE_NUMBER.key]
                ?? urlQueries[rentalFilesFields.FILE_NUMBER.key]
                ?? null
        });
    }, [rentalFile, isFetching, application, location.search, remoteCollectionId, reconciliationNumber, contract, applicationCode, storeEditedRemoteCollection]);

    // Redirection
    if (isError) {
        return <Navigate to={replaceParameters(RouteUrls.RENTAL_FILES.url, { remoteCollectionId })} />;
    }

    return (
        <Page>
            <Page.Header
                route={RouteUrls.RENTAL_FILE_DETAILS}
                i18nData={i18nData}
                isLoading={isFetching}
                ids={{ remoteCollectionId, rentalFileId }}
                routeBase={RouteUrls.REMOTE_COLLECTION}
            />
            {isFetching ? <Spinner className='full-screen-loading' /> :
                <Page.Body>
                    <Accordion type='multiple' defaultValue={['item-1']}>
                        <AccordionItem
                            className='rounded-md'
                            value='item-1'
                        >
                            <AccordionTrigger
                                className='opacity-[.8]'
                                open={openRentalFileDetails}
                                onClick={() => setOpenRentalFileDetails(!openRentalFileDetails)}
                            >
                                <div className='flex items-center gap-2'>
                                    <Icon icon={ICONS.FOLDER} />
                                    <Entity entity={'details.file_details'} namespace={nsRentalFile} />
                                </div>
                            </AccordionTrigger>
                            <AccordionContent className='justify-items-start'>
                                {
                                    rentalFile &&
                                    <RentalFileInfosPanel rentalFile={rentalFile} refetch={refetch} />
                                }
                            </AccordionContent>
                        </AccordionItem>
                    </Accordion>
                    <Accordion type='multiple' defaultValue={['item-2']}>
                        <AccordionItem
                            className='rounded-md'
                            value='item-2'
                        >
                            <AccordionTrigger
                                className='opacity-[.8]'
                                open={openRentalFileHistory}
                                onClick={() => setOpenRentalFileHistory(!openRentalFileHistory)}
                            >
                                <div className='flex items-center gap-2'>
                                    <Icon icon={ICONS.CALENDAR_RANGE} />
                                    <Entity entity={'details.history'} namespace={nsRentalFile} />
                                </div>
                            </AccordionTrigger>
                            <AccordionContent>
                                {
                                    rentalFile &&
                                    <RentalFileHistoryPanel rentalFile={rentalFile} />
                                }
                            </AccordionContent>
                        </AccordionItem>
                    </Accordion>
                </Page.Body>
            }
        </Page>
    );
}

export default RentalFileDetails;
